import {useEffect} from "react";

import {pollAssetPath} from "./pollAssetPath";

/**
 * The qset on the current survey page, for the feedback report (#1249).
 *
 * `usePostUserFeedback` renders outside the survey pages, so it has no prop
 * path to the qset; like the log batcher (`qsetLogBuffer.ts`) it reaches it
 * through this module-level slot. Registered by `usePollOrchestration` (v3
 * and v4 survey pages) and `useZooMode` (zoo galleries).
 */

interface QuestionLike {
  id?: unknown;
  type?: unknown;
  [key: string]: unknown;
}

export interface QsetLike {
  /** `object`, not `QuestionLike`: the poll asset types its questions that loosely. */
  questions?: ReadonlyArray<object>;
}

/**
 * The authored question can carry long markdown, option lists and image
 * URLs. A report is plain text through a mailer whose size limit was never
 * measured, so the props stay a bounded addition rather than the next
 * unbounded block (the log itself was taken out of the report for that).
 */
export const MAX_WIDGET_PROPS_CHARS = 4000;

/**
 * The qset together with the poll slug of the page that registered it, and
 * the CDN asset hash it was fetched by (none for a zoo fixture).
 *
 * The slug is cheap insurance, not a fix for an observed report: the survey
 * page stays mounted when the URL moves to the next poll, and although
 * `useComputeQset` resets its qset on a slug change, the registration effect
 * of that same commit still sees the old qset. Whatever is registered under a
 * slug other than the one in the URL is never described.
 */
export interface ActiveQset {
  qset: QsetLike;
  slug: string | undefined;
  assetHash?: string;
}

let active: ActiveQset | undefined;

export const getActiveQset = (): ActiveQset | undefined => active;

/**
 * The slug segment of a survey or zoo URL (`/poll/<slug>`, `/poll1/...`,
 * `/poll2/...`), read the way the feedback report's `Poll slug:` line always
 * has. Deliberately looser than `resolveQsetSlug`, which only knows the
 * logged `/poll1` and `/poll2` routes: both sides of the comparison must use
 * this one definition.
 */
export const pollSlugFromPath = (pathname: string): string | undefined =>
  pathname.split("/")[2] || undefined;

/**
 * Returns the matching unregister. It only clears the slot while this entry
 * is still the registered one, so a stale unregister (another registrant, a
 * late cleanup) can never wipe a newer qset.
 */
export const registerActiveQset = (
  qset: QsetLike | undefined,
  slug?: string,
  assetHash?: string,
): (() => void) => {
  const entry = qset ? {qset, slug, assetHash} : undefined;
  active = entry;
  return () => {
    if (active === entry) active = undefined;
  };
};

/**
 * Registers the page's qset for as long as it is mounted, following a
 * replaced qset. `null` (not loaded yet, or a zoo listing) registers nothing.
 * The slug is taken from the URL when the qset arrives, not when a report is
 * sent, so it names the poll the qset belongs to.
 */
export const useRegisterActiveQset = (
  qset: QsetLike | null | undefined,
  assetHash?: string,
): void => {
  useEffect(
    () =>
      registerActiveQset(
        qset ?? undefined,
        pollSlugFromPath(globalThis.location.pathname),
        assetHash,
      ),
    [qset, assetHash],
  );
};

/**
 * `Qset JSON:` -- the authored qset file on the CDN, so a reader opens exactly
 * what this respondent's page loaded (the asset depends on language and sex,
 * so the poll slug alone does not name it). The URL is built here rather
 * than at registration, so a survey page never reads `Config` while it
 * renders.
 */
export const formatQsetJsonLine = (
  entry: ActiveQset | undefined,
  pollSlug: string | undefined,
  cdnUrl: string,
): string => {
  if (!entry || entry.slug !== pollSlug) return "Qset JSON: unavailable";
  return entry.assetHash
    ? `Qset JSON: ${cdnUrl.replace(/\/+$/, "")}${pollAssetPath(entry.assetHash)}`
    : "Qset JSON: unavailable (not loaded from the CDN)";
};

const capProps = (json: string): string =>
  json.length <= MAX_WIDGET_PROPS_CHARS
    ? json
    : `${json.slice(0, MAX_WIDGET_PROPS_CHARS)}... (truncated, ${MAX_WIDGET_PROPS_CHARS} of ${json.length} chars)`;

/**
 * `Widget name:` / `Widget props:` lines for the question on stage: the
 * authored question as the qset JSON declares it, minus `id` (already on its
 * own line) and `type`. Randomized option order is not reflected -- that is
 * per respondent and lives in `results`.
 */
export const formatWidgetLines = (
  entry: ActiveQset | undefined,
  questionId: string | null | undefined,
  pollSlug: string | undefined,
): string => {
  if (!entry) return "Widget name: unavailable (no-qset)";
  if (entry.slug !== pollSlug) {
    return "Widget name: unavailable (qset of another poll)";
  }
  if (!questionId) return "Widget name: unavailable (no-question-id)";

  const question = entry.qset.questions?.find(
    (q) => (q as QuestionLike).id === questionId,
  ) as QuestionLike | undefined;
  if (!question) {
    return "Widget name: unavailable (question not in the loaded qset)";
  }

  const {id: _id, type, ...props} = question;
  return `Widget name: ${String(type)}\nWidget props: ${capProps(JSON.stringify(props))}`;
};
